import { create } from 'zustand'

import { ApiError } from '@/lib/api/errors'
import { isUnauthorized } from '@/lib/api/friendly-errors'
import { useSessionStore } from '@/lib/auth/session-store'
import { refreshTokenStore } from '@/lib/auth/token-store'
import { env } from '@/lib/config/env'

import { authApi } from '../api'
import { applyAuthenticatedProfile } from '../session'
import { useGateStore } from '../state/gate-store'
import { evaluateUpdate } from './update'

export type StartupPhase = 'idle' | 'running' | 'ready' | 'error'

interface StartupState {
  phase: StartupPhase
  error: unknown
  start: () => Promise<void>
  retry: () => Promise<void>
  reset: () => void
}

let inflight: Promise<void> | null = null

/**
 * Startup gate order from UI_PARITY §2:
 *
 * 1. `/app-config` (fail-open) and the update gate;
 * 2. session restore (web HttpOnly cookie, native secure refresh token);
 * 3. `/me`;
 * 4. `/auth/legal-status` (fail-closed with retry).
 *
 * Only the access token lives in memory; refresh stays in the HttpOnly cookie
 * on web and in the native secure store.
 */
export const useStartupStore = create<StartupState>()((set, get) => ({
  phase: 'idle',
  error: null,

  start: () => {
    if (get().phase === 'ready') return Promise.resolve()
    if (inflight) return inflight

    inflight = runStartup(set).finally(() => {
      inflight = null
    })
    return inflight
  },

  retry: async () => {
    useGateStore.getState().setTermsRequired(false)
    set({ phase: 'idle', error: null })
    await get().start()
  },

  reset: () => {
    inflight = null
    set({ phase: 'idle', error: null })
    useGateStore.getState().resetGates()
  },
}))

type StartupSetter = (
  partial:
    | Partial<StartupState>
    | ((state: StartupState) => Partial<StartupState>),
) => void

async function runStartup(set: StartupSetter): Promise<void> {
  set({ phase: 'running', error: null })

  // 1. App config. A transient failure must not lock out the app.
  try {
    const config = await authApi.getAppConfig()
    const decision = evaluateUpdate(config, {
      version: env.appVersion,
      platform: env.clientPlatform,
    })
    useGateStore.getState().setUpdate(decision)
    if (decision.required) {
      set({ phase: 'ready', error: null })
      return
    }
  } catch {
    useGateStore.getState().setUpdate(null)
  }

  // 2. Restore the session. Native hydrates the refresh token from the
  // platform keystore first; without a token the user is a guest.
  await refreshTokenStore.init()
  const session = useSessionStore.getState()
  if (env.authMode === 'bearer' && !refreshTokenStore.get()) {
    session.setStatus('guest')
    set({ phase: 'ready', error: null })
    return
  }

  const alreadyInMemory =
    session.status === 'authenticated' && Boolean(session.accessToken)
  if (!alreadyInMemory) {
    try {
      const tokens = await authApi.refresh()
      await useSessionStore.getState().applyTokens(tokens)
    } catch (error) {
      if (isGuestSessionError(error)) {
        useSessionStore.getState().clearSession()
        useGateStore.getState().setTermsRequired(false)
        set({ phase: 'ready', error: null })
        return
      }
      set({ phase: 'error', error })
      return
    }
  }

  // 3. Private profile.
  try {
    const profile = await authApi.getMe()
    await applyAuthenticatedProfile(profile)
  } catch (error) {
    if (isUnauthorized(error)) {
      useSessionStore.getState().clearSession()
      set({ phase: 'ready', error: null })
      return
    }
    if (
      error instanceof ApiError &&
      error.code === 'TERMS_ACCEPTANCE_REQUIRED'
    ) {
      // Some deployments gate `/me` behind terms acceptance; keep the token,
      // show the gate and let the user accept.
      const legal = await safelyLoadLegalStatus(set)
      if (legal === null) return
      set({ phase: 'ready', error: null })
      return
    }
    set({ phase: 'error', error })
    return
  }

  // 4. Legal status is fail-closed: a failure shows retry, not the app.
  const legal = await safelyLoadLegalStatus(set)
  if (legal === null) return

  set({ phase: 'ready', error: null })
}

/**
 * A 403 on the web refresh during startup means there is no usable CSRF
 * cookie (for example a first visit), so treat it as a guest rather than a
 * fatal startup error. Other web mutations still surface FORBIDDEN normally.
 */
function isGuestSessionError(error: unknown): boolean {
  return (
    isUnauthorized(error) ||
    (env.authMode === 'cookie' &&
      error instanceof ApiError &&
      error.status === 403)
  )
}

/**
 * Loads the legal status and applies the gate. Returns `null` after recording
 * a recoverable error (session cleared on 401, retry otherwise).
 */
async function safelyLoadLegalStatus(
  set: StartupSetter,
): Promise<'loaded' | null> {
  try {
    const legal = await authApi.legalStatus()
    useGateStore.getState().setLegalStatus({
      requiresTermsAcceptance: legal.requiresTermsAcceptance,
      currentTermsVersion: legal.currentTermsVersion,
    })
    return 'loaded'
  } catch (error) {
    if (isUnauthorized(error)) {
      useSessionStore.getState().clearSession()
      set({ phase: 'ready', error: null })
      return null
    }
    set({ phase: 'error', error })
    return null
  }
}
